import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import get from 'lodash/get';
import { SseClient } from 'ngx-sse-client';
import { filter, firstValueFrom, Subject, switchMap } from 'rxjs';

import { ConfigService } from './config.service';
import { LoggingService } from './logging.service';

export const AUTH_LOCAL_STORAGE_TOKEN = 'satellizer_token';
const LAST_ACTIVITY_LOCAL_STORAGE_KEY = 'flywheel_last_activity';
// session storage so each tab returns to its own page
const SESSION_TIMEOUT_PAGE_SESSION_STORAGE_KEY = 'flywheel_session_timeout_page';
const LOGGED_OUT_ROUTES = ['logged-out', 'login', 'logout'];
const defaultSessionTimeout = 12 * 60 * 60; // 12 hours

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private config = inject(ConfigService);
  private guardedHttp = inject(HttpClient);
  private router = inject(Router);
  private auth0 = inject(Auth0Service);
  private sseClient = inject(SseClient);
  private logger = inject(LoggingService);

  public tokenRefreshed$ = new Subject<void>(); // emits when the auth0 token is refreshed
  private auth0iat?: number;
  private authRefreshHandle: null | ReturnType<typeof setTimeout> = null;
  private initialAuthComplete = false; // used to prevent multiple calls to /api/login/auth0
  private resetSessionTimeoutChannel = new BroadcastChannel('reset_session_timeout');
  private sessionTimeoutRef?: ReturnType<typeof setTimeout>;
  private lastActivityAt = new Date();
  private sessionTimedOut = false; // set once we start logging out due to inactivity
  private unguardedHttp: HttpClient; // makes requests without attaching auth

  constructor() {
    const httpBackend = inject(HttpBackend);
    const auth0 = this.auth0;

    this.unguardedHttp = new HttpClient(httpBackend);
    auth0.idTokenClaims$.subscribe((idToken) => {
      this.auth0iat = idToken?.iat;
    });

    this.resetSessionTimeout();
    this.resetSessionTimeoutChannel.addEventListener('message', () => {
      // make sure we don't rebroadcast to the sender
      this.resetSessionTimeout({ broadcast: false });
    });

    this.auth0.error$.subscribe((error) => {
      console.error('Auth0 error', error); // eslint-disable-line no-console

      if (this.isSessionExpiredError(error as Error)) {
        this.sessionInactive();
      }
    });
  }

  get isLoggedIn(): boolean {
    return this.getBearerToken()?.length > 0;
  }

  /**
   * True if the user has been inactive for longer than the inactivity timeout.
   * Browsers throttle or freeze timers in background/sleeping tabs, so the session timeout timer can't be relied on alone.
   */
  get hasSessionTimedOut(): boolean {
    if (this.sessionTimedOut) {
      return true;
    }
    const inactivityTimeoutMs = this.inactivityTimeout * 1000;
    // activity in other tabs counts too
    const sharedLastActivity = new Date(localStorage.getItem(LAST_ACTIVITY_LOCAL_STORAGE_KEY) ?? 0).getTime() || 0;
    const lastActivity = Math.max(this.lastActivityAt.getTime(), sharedLastActivity);
    return this.isLoggedIn && inactivityTimeoutMs > 0 && Date.now() - lastActivity > inactivityTimeoutMs;
  }

  private get inactivityTimeout(): number {
    // timeout in seconds
    const siteTimeout =
      parseInt(String(get(this.config, 'apiConfig.site.inactivity_timeout'))) || defaultSessionTimeout;
    const overrideTimeout = parseInt(String(this.config.getFeatureSetting('override_inactivity_timeout')));
    if (overrideTimeout > 0 && overrideTimeout < siteTimeout) {
      return overrideTimeout;
    }
    return siteTimeout;
  }

  sseListener = this.auth0.idTokenClaims$
    .pipe(
      filter((idToken) => Boolean(idToken)),
      switchMap((_) => this.sseClient.stream('/api/events')),
    )
    .subscribe((event) => {
      if (event.type === 'user_disabled') {
        this.logout(window.location.origin + '/#/logged-out/deactivated');
      }
    });

  /**
   * Process the auth0 token from one localStorage item to a satellized equivelent
   * This is required for exchange/ohif viewers/service workers to authenticate!
   */
  storeAuth0Token(): void {
    const storage = { ...localStorage };
    const storageKeys = Object.keys(storage);
    // escape (prepend a backslash to) any special (regex) characters in the audience
    const escapedAudience = this.config.authAudience.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const tokenRegex = new RegExp(`@@auth0spajs@@.*::${escapedAudience}`);
    // Find all matching tokens and select the one with the greatest expiresAt
    let maxExpiresAt = -1;
    let newestAuthItem = null;
    for (const key of storageKeys) {
      if (tokenRegex.test(key)) {
        try {
          const authItem = JSON.parse(storage[key]) || {};
          if (authItem?.expiresAt && authItem.expiresAt > maxExpiresAt) {
            maxExpiresAt = authItem.expiresAt;
            newestAuthItem = authItem;
          } else if (authItem?.body?.access_token && !authItem.expiresAt && newestAuthItem === null) {
            newestAuthItem = authItem; // fallback to first item if no expiresAt found
          }
        } catch (e) {
          // ignore parse errors
        }
      }
    }
    if (newestAuthItem) {
      const newToken = newestAuthItem?.body?.access_token;
      if (!newToken) {
        return;
      }
      this.setToken(`Bearer ${newToken}`);
      // refreshing 600s before exp
      const now = Date.now();
      const refreshDelay = newestAuthItem?.expiresAt ? newestAuthItem.expiresAt * 1000 - now - 600000 : 0;
      if (refreshDelay > 0) {
        this.scheduleTokenRefresh(refreshDelay);
      }
    }
  }

  /**
   * Store a non-auth0 token for auth
   */
  async setToken(newToken: string): Promise<void> {
    const oldToken = this.getBearerToken();
    const isSameToken = oldToken === newToken;

    if (!this.initialAuthComplete && this.config.isAuth0Enabled()) {
      this.logAuth0Login();
      this.initialAuthComplete = true;
    }

    if (isSameToken) {
      return;
    }

    this.storeSatellizedToken(newToken);

    this.tokenRefreshed$.next();

    if (oldToken) {
      this.invalidateToken(oldToken, true);
    }
  }

  /**
   * Parse the satellized token and return the id_token in a Bearer format
   */
  getBearerToken(): string {
    // auth0 tokens are now being stashed in the satellizer token as well
    const item = localStorage.getItem(AUTH_LOCAL_STORAGE_TOKEN);
    return this.decodeSatellizedToken(item);
  }

  getBasicToken(): string | null {
    let token = this.getBearerToken();
    if (token) {
      token = token.replace(/^Bearer /, ''); // remove the 'Bearer ' prefix for basic auth
    }
    return token;
  }

  /**
   * Sends the user to auth0 on login, or to the local login page if auth0 is not enabled
   * @param target - optional url fragment to redirect to after login, defaults to the root
   */
  login(target: string = '/'): void {
    if (!this.config.isAuth0Enabled()) {
      this.router.navigate(['/login'], target === '/' ? {} : { queryParams: { redirect: target } });
      return;
    }

    // router.url can be '/' when the auth guard has cancelled navigation
    this.auth0.loginWithRedirect({ authorizationParams: { prompt: 'login' }, appState: { target } });
  }

  /**
   * Invalidate the current token and logs the user out.
   * @param returnTo URL to navigate to after logout, must be a full URL, defaults to the root of the current origin
   */
  async logout(returnTo?: string): Promise<void> {
    this.restartSessionTimer({ broadcast: true });
    await this.invalidateToken(this.getBearerToken());
    localStorage.removeItem(AUTH_LOCAL_STORAGE_TOKEN);
    if (this.config.isAuth0Enabled()) {
      this.auth0.logout({
        logoutParams: {
          returnTo: returnTo || window.location.origin,
        },
      });
      return;
    }
    this.sessionTimedOut = false; // no page reload without auth0, so allow logging back in
    this.router.navigate([returnTo || '/']);
  }

  /**
   * Marks the session as active, or logs the user out if they've already been inactive too long
   */
  resetSessionTimeout({ broadcast = true } = {}): void {
    if (this.hasSessionTimedOut) {
      this.sessionInactive();
      return;
    }
    this.restartSessionTimer({ broadcast });
  }

  private restartSessionTimer({ broadcast }: { broadcast: boolean }): void {
    clearTimeout(this.sessionTimeoutRef);
    this.lastActivityAt = new Date();
    localStorage.setItem(LAST_ACTIVITY_LOCAL_STORAGE_KEY, this.lastActivityAt.toISOString());
    const inactivityTimeoutMs = this.inactivityTimeout * 1000;
    if (inactivityTimeoutMs > 0) {
      this.sessionTimeoutRef = setTimeout(() => {
        this.logger.log('Flywheel internal session timeout triggered');
        this.sessionInactive();
      }, inactivityTimeoutMs);
      if (broadcast) {
        this.resetSessionTimeoutChannel.postMessage(inactivityTimeoutMs);
      }
    }
  }

  private storeSatellizedToken(token: string): void {
    localStorage.setItem(AUTH_LOCAL_STORAGE_TOKEN, this.satellizeToken(token));
  }

  private async logAuth0Login(): Promise<void> {
    try {
      await firstValueFrom(this.guardedHttp.get<null>(this.config.getApiUrl('/api/login/auth0')));
    } catch (error) {
      this.logger.warn('Failed to log Auth0 login:', error);
    }
  }

  private scheduleTokenRefresh(refreshInterval: number): void {
    if (this.authRefreshHandle) {
      clearTimeout(this.authRefreshHandle);
    }
    this.authRefreshHandle = setTimeout(async () => {
      const now = Math.floor(Date.now() / 1000);
      const timeoutExceeded = this.auth0iat && now > this.auth0iat + this.inactivityTimeout;

      if (timeoutExceeded) {
        this.logger.log('Auth token reached Auth0 inactivity timout');
        return this.sessionInactive();
      }

      try {
        await firstValueFrom(this.auth0.getAccessTokenSilently({ cacheMode: 'off' }));
        this.storeAuth0Token();
      } catch (error) {
        this.logger.warn('Token refresh failed:', error);
        if (this.isSessionExpiredError(error as Error)) {
          this.sessionInactive();
        }
      }
    }, refreshInterval);
  }

  /**
   *
   * @param token - expects a string starting with `Bearer `
   * @returns encoded string for localstorage
   */
  private satellizeToken(token: string): string {
    const reversedToken = this.reverseString(btoa(this.reverseString(token)));
    const padding = (4 - (reversedToken.length % 4)) % 4;
    const paddedToken = reversedToken + '='.repeat(padding);
    return `v1:${paddedToken}:${padding}`;
  }

  /**
   *
   * @param token - expects a string matching the output format of satellizeToken
   * @returns decoded string starting with `Bearer `
   */
  private decodeSatellizedToken(token?: string | null): string {
    if (!token || !token.startsWith('v1:')) {
      return token || '';
    }
    let [, value, padding] = token.split(':'); // eslint-disable-line
    for (let i = 0; i < parseInt(padding, 10); i++) {
      value = '=' + value;
    }
    const fullToken = this.reverseString(atob(this.reverseString(value)));
    const parts = fullToken.split(' ');
    const tokenStub = parts[1] || parts[0];
    return `Bearer ${tokenStub}`;
  }

  private reverseString(str: string): string {
    return Array.from(str).reverse().join('');
  }

  private async invalidateToken(token?: string, refreshOnly = false): Promise<void> {
    if (token) {
      await firstValueFrom(
        this.unguardedHttp.post<null>(this.config.getApiUrl(`/api/logout?refresh_only=${refreshOnly}`), null, {
          headers: { Authorization: token },
        }),
      ).catch((error) => {
        this.logger.warn(error);
      });
    }
  }

  private hasLoginRequiredError(error: Error): boolean {
    return ['login_required', 'login required', 'consent_required', 'consent required', 'timeout'].includes(
      (error?.message || '').toLowerCase().trim(),
    );
  }

  /**
   * A login_required error only means the session expired if the user had a session to begin with.
   * Fresh, logged-out visitors get the same error and are sent to login by the AuthGuard instead.
   */
  private isSessionExpiredError(error: Error): boolean {
    return this.isLoggedIn && this.hasLoginRequiredError(error) && !this.isLoggedOutRoute();
  }

  // the app uses hash routing, so logged-out routes live in the hash (e.g. /#/logged-out/session-timeout)
  private isLoggedOutRoute(): boolean {
    return window.location.pathname.startsWith('/logged-out') || window.location.hash.startsWith('#/logged-out');
  }

  /**
   * Logs the user out to the session-timeout page
   * @param lastPage - url fragment the user was on, used to send them back after they log in again
   */
  sessionInactive(lastPage: string = this.router.url): void {
    if (this.sessionTimedOut) {
      return; // already logging out
    }
    this.sessionTimedOut = true;
    if (this.isReturnablePage(lastPage)) {
      sessionStorage.setItem(SESSION_TIMEOUT_PAGE_SESSION_STORAGE_KEY, lastPage);
    }
    this.logout(window.location.origin + '/#/logged-out/session-timeout');
  }

  /**
   * Sends the user to login, returning them to the page they were on when their session timed out
   */
  loginAfterSessionTimeout(): void {
    const lastPage = sessionStorage.getItem(SESSION_TIMEOUT_PAGE_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_TIMEOUT_PAGE_SESSION_STORAGE_KEY);
    this.login(this.isReturnablePage(lastPage) ? lastPage : '/');
  }

  // only in-app pages are worth returning to, logged-out pages would loop the user back out
  private isReturnablePage(url?: string | null): url is string {
    if (!url?.startsWith('/') || url.startsWith('//')) {
      return false;
    }
    const firstSegment = url.split(/[/?#;]/)[1];
    return Boolean(firstSegment) && !LOGGED_OUT_ROUTES.includes(firstSegment);
  }
}
